import { createContext, useContext } from "react";
import { readHomepageFeatureDataFromCache } from "HomePage/modules/HomePageGraphqlHelpers";
import { HOMEPAGE_FEATURE_KEY_MAP } from "HomePage/modules/HomePageConstants";

import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";

import {
  getUserCurriculumPrograms,
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";

import {
  HOME_PAGE_FEATURE_CLASSROOM_TODO,
  HOME_PAGE_FEATURE_TIMETABLE,
} from "Courses/components/TeacherCourses/Constants";

import { SNPv2_PERM } from "Constants/permissionConstants";
import { store } from "store/createStore";

import _ from "lodash";

export const SMALLEST_BREAKPOINT = 900;

export const MEDIUM_BREAKPOINT = 1280;

export const LARGE_BREAKPOINT = 1440;

export const EXTRA_LARGE_BREAKPOINT = 1728;

export const getContainerPadding = width => {
  if (width <= SMALLEST_BREAKPOINT) {
    return [40, 0];
  }
  if (width <= MEDIUM_BREAKPOINT) {
    return [112, 0];
  }
  if (width <= LARGE_BREAKPOINT) {
    return [112, 0];
  }
  if (width <= EXTRA_LARGE_BREAKPOINT) {
    return [256, 0];
  }
  return [260, 0];
};

export const getWidgetBreakpoint = ({ widgetId, layout, widgetsMetadata }) => {
  if (widgetsMetadata?.[widgetId]?.breakpoint) {
    const breakpointMetadata = widgetsMetadata[widgetId].breakpoint;
    const widgetLayout = _.find(layout, { i: widgetId });
    const breakpointKeys = _.keys(widgetsMetadata[widgetId].breakpoint);
    let currentBreakpoint = null;

    const sortedBreakpointKeys = _.sortBy(
      breakpointKeys,
      key => breakpointMetadata[key]
    );

    _.forEach(sortedBreakpointKeys, key => {
      if (widgetLayout.w <= breakpointMetadata[key]) {
        currentBreakpoint = key;
        return false;
      }
    });
    return currentBreakpoint;
  }
  return null;
};

export const getBreakpointsMap = ({ layout, widgetsMetadata }) => {
  return _.reduce(
    layout,
    (result, { i }) => {
      const currentBreakpoint = getWidgetBreakpoint({
        widgetId: i,
        layout,
        widgetsMetadata,
      });

      if (currentBreakpoint) {
        return { ...result, [i]: currentBreakpoint };
      }

      return result;
    },
    {}
  );
};

export const HOME_PAGE_CURRICULUM_LEVEL_CONFIGURATIONS = [SNPv2_PERM];

export const getSelectedCurriculumProgramIds = state => {
  if (getIsCombineCurriculumHomePageEnabled()) {
    const userCurriculumPrograms = getUserCurriculumPrograms();
    return _.map(userCurriculumPrograms, "id");
  }
  return [getSelectedCurriculumId(state)];
};

export const getSelectedCurriculumProgramTypes = state => {
  if (getIsCombineCurriculumHomePageEnabled()) {
    const userCurriculumPrograms = getUserCurriculumPrograms();
    return _.map(userCurriculumPrograms, "type");
  }
  return [getCurrentCurriculumProgramType(state)];
};

export const getVariablesForHomePageConfigurations = ({
  curriculumPrograms,
}) => {
  const contexts = [];

  const programsCount = _.size(curriculumPrograms);

  for (let i = 0; i < programsCount; i++) {
    const { id } = curriculumPrograms[i];

    const context = [
      {
        entityId: id,
        entityType: "CURRICULUM_PROGRAM",
      },
    ];

    contexts.push(context);
  }

  return {
    filters: {
      module: "TEACHER_HOMEPAGE",
      appType: ["TEACHER_WEB"],
      contexts,
    },
  };
};

export const getWidgetVisibilityTooltipContent = ({
  isCourseHomePageVisibleForStudent,
  isCourseHomePageVisibleForParent,
  t,
}) => {
  //If visibility of homepage is off for both user types - student & parent
  if (!isCourseHomePageVisibleForStudent && !isCourseHomePageVisibleForParent) {
    return t("common:widget_not_visible_to", {
      label: t("common:join_array_with_and", {
        array: [
          t("common:student_plural_lowercase"),
          t("common:family_member_plural_lowercase"),
        ],
      }),
    });
  }
  // If not enabled for student user type
  else if (!isCourseHomePageVisibleForStudent) {
    return t("common:widget_not_visible_to", {
      label: t("common:student_plural"),
    });
  }
  // If not enabled for parent user type
  else if (!isCourseHomePageVisibleForParent) {
    return t("common:widget_not_visible_to", {
      label: t("common:family_member_plural"),
    });
  }

  return null;
};

export const shouldShowWidget = ({ widgetType, widgetsData }) => {
  return _.get(widgetsData, `${widgetType}.showWidget`, true);
};

export const isWidgetVisible = ({ widgetType, widgetsData, WIDGET_TYPES }) => {
  const { BANNER, RICH_TEXT_EDITOR, QUICK_LINKS } = WIDGET_TYPES;

  switch (widgetType) {
    case BANNER:
      return shouldShowWidget({ widgetType: BANNER, widgetsData });
    case RICH_TEXT_EDITOR:
      return shouldShowWidget({
        widgetType: RICH_TEXT_EDITOR,
        widgetsData,
      });
    case QUICK_LINKS:
      return shouldShowWidget({
        widgetType: QUICK_LINKS,
        widgetsData,
      });
    default:
      return false;
  }
};

export const ResizeContext = createContext(false);

export const useResizeContext = () => {
  return useContext(ResizeContext);
};

export const getHomepageModuleDetails = moduleKey => {
  return readHomepageFeatureDataFromCache({
    key: HOMEPAGE_FEATURE_KEY_MAP[moduleKey],
  });
};

export const getHomepageFeatureVisibilityStatus = featureKey => {
  const featureDetails = getHomepageModuleDetails(featureKey);
  const isEnabled = _.get(featureDetails, "isEnabled", false);

  const homePageWidgetPreferenceMapping = _.get(
    store.getState(),
    "platform.homePageWidgetPreferences",
    {}
  );

  switch (featureKey) {
    case HOME_PAGE_FEATURE_TIMETABLE: {
      const canViewTimetableWidget = _.get(
        homePageWidgetPreferenceMapping,
        "TIME_TABLE",
        false
      );

      return isEnabled && !canViewTimetableWidget ? "VISIBLE" : "NOT_VISIBLE";
    }
    case HOME_PAGE_FEATURE_CLASSROOM_TODO: {
      const canViewADMWidget = _.get(
        homePageWidgetPreferenceMapping,
        "ASSIGNMENT_DEADLINE_WIDGET",
        false
      );

      return isEnabled && !canViewADMWidget ? "VISIBLE" : "NOT_VISIBLE";
    }
    default:
      return isEnabled ? "VISIBLE" : "NOT_VISIBLE";
  }
};

export const checkIsHomePageFeatureEnabled = featureKey => {
  const featureDetails = getHomepageModuleDetails(featureKey);
  return _.get(featureDetails, "isEnabled", false);
};

export const checkIsHomepageWidgetEnabled = widgetKey => {
  if (!getIsCombineCurriculumHomePageEnabled()) return false;

  const widgetDetails = readHomepageFeatureDataFromCache({
    key: widgetKey,
  });

  return _.get(widgetDetails, "isEnabled", false);
};

export const getProgramListForModule = moduleKey => {
  const featureDetails = getHomepageModuleDetails(moduleKey);

  const { metadata = {} } = featureDetails || {};

  const {
    enabledCurriculumProgramIds = [],
    canManageCurriculumProgramIds = [],
  } = metadata;

  return {
    enabledCurriculumProgramIds,
    canManageCurriculumProgramIds,
    manageableEnabledProgramIds: _.intersection(
      enabledCurriculumProgramIds,
      canManageCurriculumProgramIds
    ),
  };
};
